import { FC, ReactElement, useState } from 'react'
import styled from 'styled-components'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { device } from '../breakpoints'
import CanvasVideoPlayerB from '../CanvasVideoPlayerB'
import { useTransition, animated } from '@react-spring/web'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'

const LabelText = styled.div`
  font-size: 1.2em;
  font-weight: 700;
`

const LabelTextSmall = styled.div`
  font-size: 1.2em;
  font-weight: 400;
`

const labels = [
  {
    name: 'state',
    markers: [
      {
        html: <LabelText>Future excavation</LabelText>,
        position: {
          desktop: {
            x: 0.55,
            y: 0.45,
          },
          mobile: {
            x: 0.35,
            y: 0.48,
          },
        },
      },
    ],
  },
  {
    name: 'cathedral',
    markers: [
      {
        html: <LabelText>Cathédrale de Lausanne</LabelText>,
        position: {
          desktop: {
            x: 0.6,
            y: 0.35,
          },
          mobile: {
            x: 0.55,
            y: 0.32,
          },
        },
      },
      {
        html: <LabelTextSmall>80 mètres</LabelTextSmall>,
        position: {
          desktop: {
            x: 0.27,
            y: 0.35,
          },
          mobile: {
            x: 0.15,
            y: 0.35,
          },
        },
      },
    ],
  },
  {
    name: 'Empty',
    markers: [
      {
        html: <LabelText></LabelText>,
        position: {
          desktop: {
            x: 0.5,
            y: 0.5,
          },
          mobile: {
            x: 0.5,
            y: 0.5,
          },
        },
      },
    ],
  },
]

const ExampleComponent: FC = (): ReactElement => {
  const isMobile = window.innerWidth < 768
  const [canvasIsLoading, setCanvasIsLoading] = useState(true)
  const [started, setStarted] = useState(false)
  const [stage, setStage] = useState(0)
  const [progress, setProgress] = useState(0)
  const [canvasBoundingClientRect, setCanvasBoundingClientRect] =
    useState<DOMRect>()

  const onStepProgress = ({ progress }: { progress: any }) => {
    if (stage < 3) {
      setProgress(progress)
    }
  }

  const onStepEnter = ({
    data,
    direction,
  }: {
    data: any
    direction: string
  }) => {
    console.log('ENTER', data, direction)
    setStage(data)
    if (data === 0) {
      setStarted(true)
    } else {
      setStarted(false)
    }
  }

  const onStepExit = ({
    data,
    direction,
  }: {
    data: any
    direction: string
  }) => {
    console.log('EXIT', data, direction)
    if (data === 0 && direction === 'up') {
      setStarted(false)
    }
  }

  const activeLabel = labels[stage === 1 ? 0 : stage === 3 ? 1 : 2]

  const transitions = useTransition(activeLabel, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  const globalProgress = stage < 3 ? progress / 3 + stage / 3 : 1

  return (
    <Container>
      <ScrollArea>
        <VideoCanvas>
          <Overlay>
            {transitions((style, item) => {
              return item.markers.map((m, i) => (
                <Label
                  key={`label-${item.name}-${i}`}
                  style={{
                    opacity: style.opacity,
                    top:
                      m.position[isMobile ? 'mobile' : 'desktop'].y * 100 + '%',
                    left:
                      m.position[isMobile ? 'mobile' : 'desktop'].x * 100 + '%',
                  }}
                >
                  {m.html}
                </Label>
              ))
            })}
            {/* <div>
            {stage} - {progress.toFixed(2)} - {globalProgress.toFixed(2)}
          </div> */}
          </Overlay>
          <CanvasVideoPlayerB
            started={started}
            onLoaded={() => {
              setCanvasIsLoading(false)
            }}
            onMounted={(boundingClientRect: DOMRect | undefined) => {
              setCanvasBoundingClientRect(boundingClientRect)
            }}
            progress={globalProgress}
          />
        </VideoCanvas>
        <ScrollamaContainer>
          <Scrollama
            offset={1}
            onStepEnter={onStepEnter}
            onStepProgress={onStepProgress}
            onStepExit={onStepExit}
          >
            <Step data={0}>
              <ScrollContainerA>
                <ScrollText>
                  <p>
                    En huitante ans, étape par étape, l’exploitation du calcaire a
                    creusé un gouffre de plus de{' '}
                    <Colored>800 mètres de long</Colored> dans la colline
                    vaudoise, célèbre pour avoir connu la première «zone à
                    défendre» de Suisse en 2021.
                  </p>
                  <p>
                    Après six ans de procédures judiciaires, Holcim a obtenu du
                    Tribunal fédéral une nouvelle extension sur le plateau de la
                    Birette.
                  </p>
                </ScrollText>
              </ScrollContainerA>
            </Step>
            <Step data={1}>
              <ScrollContainerB>
                <ScrollText>
                  Cette future excavation touche la dernière grande zone qui
                  n’est pas située en territoire protégé. Elle doit être
                  exploitée jusqu’en 2060, selon le cimentier, et permettre
                  d’extraire en tout cas{' '}
                  <Colored>2,8 millions de m<sup>3</sup> de calcaire</Colored>.
                </ScrollText>
              </ScrollContainerB>
            </Step>
            <Step data={2}>
              <ScrollContainerC>
                <ScrollText>
                  Si rien n’est réaménagé d’ici là, la carrière du Mormont sera
                  alors un <Colored>immense gouffre de 1,2 km de long</Colored>,
                  avec peu d’équivalents en Suisse. Pour donner une idée: la
                  cathédrale de Lausanne pourrait y rentrer sans peine.
                </ScrollText>
              </ScrollContainerC>
            </Step>
            <Step data={3}>
              <ScrollContainerD>
                <EndElement>&nbsp;</EndElement>
              </ScrollContainerD>
            </Step>
          </Scrollama>
        </ScrollamaContainer>
      </ScrollArea>
      <TextWidth>
        <Source>
          <b>Source</b>: Swisstopo, Mosini et Caviezel SA, Tamedia
        </Source>
      </TextWidth>
    </Container>
  )
}

const Container = styled.div`
  margin-bottom: 60px;
  position: relative;
`

const Source = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 1.5rem;
  -webkit-font-smoothing: auto;
  padding-top: 1.5rem;
  line-height: 1.2;
`

const ScrollArea = styled.div`
  position: relative;
`

const VideoCanvas = styled.div`
  position: sticky;
  z-index: -1;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
`

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`

const ScrollContainerA = styled(ScrollContainer)``

const ScrollContainerB = styled(ScrollContainer)``

const ScrollContainerC = styled(ScrollContainer)``

const ScrollContainerD = styled(ScrollContainer)``

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  top: 0;
  z-index: 2;
  overflow: hidden;
`

const ScrollamaContainer = styled.div`
  position: relative;
`

const Colored = styled.span`
  color: #a757a3;
  -webkit-font-smoothing: auto;
`

const ScrollText = styled.div`
  padding: 20px;
  background-color: #ffffffee;
  font-family: var(--font-serif);
  font-size: 1em;
  line-height: 1.45;
  max-width: 500px;
  margin-bottom: 100vh;
  color: #202346;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
  }

  @media ${device.tablet}, @media ${device.laptop} {
  }
`

const EndElement = styled.div`
  margin-bottom: 100vh;
`

const Label = styled(animated.div)`
  position: absolute;
  width: 200px;
  line-height: 1.1;
  color: white;
  text-shadow: 0 0 6px rgba(0, 0, 0, 0.65);
  font-family: var(--font-sans-serif);
`

export default ExampleComponent
